import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Button, Menu as AntMenu, Switch, Tag } from 'antd';
import { MinusOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Bidding.scss';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { bidingService } from "../../services/BidingService";
import { loginService } from "../../services/LoginService";
import { useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import Header from "../../components/Header/Header";
import {
    buildCategoryOptionsFromEndpoint,
    createBackendCategoryPathResolver,
    createBackendCategoryResolver,
    filterProductsBySearchAndCategories,
    mergeCategoryOptionsWithProducts,
    resolveStatusTagProps
} from './biddingFilters';
import { useDesktopTableScroll } from './useDesktopTableScroll';
import {
    buildLastWinningPriceMap,
    createLastWinningPriceColumn,
    mergeLastWinningPricesIntoProducts
} from './renderLastWinningPriceCell';
import {
    buildAutocompleteOfferPriceMap,
    mergeAutocompletePricesIntoOfferInputs
} from './autocompleteOfferPrices';
import { createProductNameCellRenderer } from './renderProductNameCell';
import SupplierMenu from '../../components/SupplierMenu/SupplierMenu'
import { useActiveBiddingGuard } from './useActiveBiddingGuard';
const { Search } = Input;
const OfertedProducts = () => {
    const navigate = useNavigate();
    const [endTime, setEndTime] = useState(null);
    const [timeLeft, setTimeLeft] = useState('');
    const { id } = useParams();
    const location = useLocation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState(['all']);
    const [offerPrices, setOfferPrices] = useState({});
    const [isAutocompleteEnabled, setIsAutocompleteEnabled] = useState(false);
    const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
    const [autocompleteOfferPriceMap, setAutocompleteOfferPriceMap] = useState({});
    const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
    const [listId, setListId] = useState(null);
    const [biddingCounts, setBiddingCounts] = useState(null);
    const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
    const [lastWinningPriceMap, setLastWinningPriceMap] = useState({});
    const { canLoadBiddingData } = useActiveBiddingGuard(id, navigate);
    const pathname = location.pathname;
    let selectedMenuKey = null;
    if (pathname.startsWith('/produse-ofertare')) {
        selectedMenuKey = 'produse';
    } else if (pathname.startsWith('/produse-ascunse')) {
        selectedMenuKey = 'ascunse';
    } else if (pathname.startsWith('/oferte-inregistrate')) {
        selectedMenuKey = 'oferte';
    }
    const refreshBiddingCounts = useCallback(() => {
        if (!id) return Promise.resolve();
        return bidingService.getBiddingCounts(id)
            .then((res) => {
                setBiddingCounts(res?.data || null);
            })
            .catch((err) => {
                console.warn('Failed to refresh bidding_counts', err);
            });
    }, [id]);

    useEffect(() => {
        if (id && canLoadBiddingData) {
            setLoading(true);
            refreshBiddingCounts();
            bidingService.getBiddingCategories(id)
                .then((categoryRes) => {
                    setApiCategoryOptions(
                        buildCategoryOptionsFromEndpoint(categoryRes?.data, ['offering_products'])
                    );
                    return bidingService.getList(id).then((listRes) => ({ categoryData: categoryRes?.data, listData: listRes?.data }));
                })
                .then(({ categoryData, listData }) => {
                    const items = listData?.necessity_list_items || [];
                    const resolveCategoryLabel = createBackendCategoryResolver(categoryData);
                    const resolveCategoryPath = createBackendCategoryPathResolver(categoryData);
                    setListId(listData.id);
                    const formattedProducts = items.map(item => ({
                        key: item.id,
                        name: item.product?.name,
                        barcode: item.product?.barcode,
                        category: resolveCategoryLabel(item),
                        category_path: resolveCategoryPath(item),
                        quantity: item.quantity,
                        unit_measurement:
                            item.unit_measurement ||
                            item.product?.unit_measurement ||
                            item.product?.um ||
                            item.product?.unit ||
                            '',
                        instructions: item.supplementary_info,
                        statusTag: resolveStatusTagProps(item.status),
                        hasBid: !!item.current_user_bid,
                        offer: item.current_user_bid?.price ?? null,
                        originalData: item
                    }));
                    const initialOfferPrices = {};
                    formattedProducts.forEach(product => {
                        if (product.offer !== null) {
                            initialOfferPrices[product.key] = product.offer;
                        }
                    });
                    setProducts(formattedProducts);
                    setOfferPrices(initialOfferPrices);
                    if (listData.bidding_end_time) {
                        setEndTime(new Date(listData.bidding_end_time));
                    }
                })
                .catch((err) => {
                    console.error("Error fetching list/categories:", err);
                    toast.error("A apărut o eroare la încărcarea listei sau categoriilor.");
                })
                .finally(() => setLoading(false));
        }
    }, [id, canLoadBiddingData, refreshBiddingCounts]);
    useEffect(() => {
        if (!id || !canLoadBiddingData) return;

        bidingService.getLastWinningPrices(id)
            .then((res) => {
                setLastWinningPriceMap(buildLastWinningPriceMap(res?.data));
            })
            .catch((err) => {
                console.warn('Failed to fetch last winning prices', err);
            });
    }, [id, canLoadBiddingData]);
    useEffect(() => {
        setProducts((previousProducts) => {
            const nextProducts = mergeLastWinningPricesIntoProducts(previousProducts, lastWinningPriceMap);
            const hasChanges = nextProducts.some((product, index) => (
                product.lastWinningPrice !== previousProducts[index]?.lastWinningPrice
                || product.lastWinningWonAt !== previousProducts[index]?.lastWinningWonAt
            ));

            return hasChanges ? nextProducts : previousProducts;
        });
    }, [products, lastWinningPriceMap]);
    useEffect(() => {
        if (!isAutocompleteEnabled || !id || !canLoadBiddingData) return;

        setIsAutocompleteLoading(true);
        bidingService.getSupplierAutocompleteOffers(id)
            .then((res) => {
                setAutocompleteOfferPriceMap(buildAutocompleteOfferPriceMap(res?.data));
            })
            .catch((err) => {
                console.warn('Failed to fetch supplier autocomplete offers', err);
                toast.error('A aparut o eroare la incarcarea preturilor autocomplete.');
            })
            .finally(() => {
                setIsAutocompleteLoading(false);
            });
    }, [isAutocompleteEnabled, id, canLoadBiddingData]);
    useEffect(() => {
        if (!isAutocompleteEnabled) return;
        if (!Object.keys(autocompleteOfferPriceMap).length) return;

        setOfferPrices((previousOfferPrices) => (
            mergeAutocompletePricesIntoOfferInputs(products, previousOfferPrices, autocompleteOfferPriceMap)
        ));
    }, [isAutocompleteEnabled, autocompleteOfferPriceMap, products]);
    useEffect(() => {
        if (isAutocompleteEnabled) return;

        setOfferPrices((previousOfferPrices) => {
            const nextOfferPrices = { ...(previousOfferPrices || {}) };
            let changed = false;

            products.forEach((product) => {
                if (!product?.hasBid && Object.prototype.hasOwnProperty.call(nextOfferPrices, product.key)) {
                    delete nextOfferPrices[product.key];
                    changed = true;
                }
            });

            return changed ? nextOfferPrices : previousOfferPrices;
        });
    }, [isAutocompleteEnabled, products]);

    const pickBiddingCount = (...keys) => {
        const roots = [biddingCounts, biddingCounts?.counts, biddingCounts?.data];
        for (const root of roots) {
            if (!root) continue;
            for (const k of keys) {
                const v = root?.[k];
                if (v === null || v === undefined) continue;
                const parsedValue = Number(v);
                if (Number.isFinite(parsedValue)) return parsedValue;
            }
        }
        return undefined;
    };

    const categoryOptions = useMemo(
        () => mergeCategoryOptionsWithProducts(apiCategoryOptions, products),
        [apiCategoryOptions, products]
    );
    const filteredProducts = useMemo(
        () => filterProductsBySearchAndCategories(products, searchQuery, selectedCategories),
        [products, searchQuery, selectedCategories]
    );
    const { tableContentRef, footerRef, tableScrollConfig } = useDesktopTableScroll([filteredProducts.length]);
    const renderProductNameCell = useMemo(() => createProductNameCellRenderer(), []);
    const pendingBulkBids = useMemo(
        () => products
            .filter((product) => !product.hasBid)
            .map((product) => {
                const rawPrice = offerPrices[product.key];
                const parsedPrice = Number(rawPrice);
                if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
                    return null;
                }

                return {
                    key: product.key,
                    necessity_list_item_id: product.originalData?.id,
                    price: parsedPrice,
                    comment: ''
                };
            })
            .filter((bid) => bid && bid.necessity_list_item_id),
        [products, offerPrices]
    );
    const canSubmitBulkBids = pendingBulkBids.length >= 2 && !isBulkSubmitting;
    useEffect(() => {
        if (!endTime) return;
        const interval = setInterval(() => {
            const now = new Date();
            const difference = endTime - now;
            if (difference <= 0) {
                clearInterval(interval);
                setTimeLeft("00:00");
            } else {
                const minutes = Math.floor(difference / 1000 / 60);
                const seconds = Math.floor((difference / 1000) % 60);
                setTimeLeft(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} minute`);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [endTime]);
    const handleSendOffer = (record) => {
        const price = offerPrices[record.key];
        if (!price) {
            toast.error("Please enter a price before submitting the offer.");
            return;
        }
        const productData = {
            product_id: record.originalData.product.id,
            price: parseFloat(price),
            comment: "",
            idProduct: record.originalData.id
        };
        bidingService.postBid(listId, productData)
            .then(() => {
                toast.success(`Offer submitted for ${record.name} at ${price} RON`);
                setProducts(prev => prev.filter(p => p.key !== record.key));
                refreshBiddingCounts();
            })
            .catch((error) => {
                console.error("Error submitting offer:", error);
                toast.error("There was an error submitting your offer.");
            });
    };

    const handleSearch = (value) => {
        setSearchQuery(value || '');
    };
    const handleBulkSendOffers = () => {
        if (!listId) {
            toast.error('Lista nu este disponibila pentru trimitere.');
            return;
        }
        if (pendingBulkBids.length < 2) {
            toast.error('Completeaza cel putin 2 oferte pentru trimiterea bulk.');
            return;
        }

        setIsBulkSubmitting(true);
        bidingService.bulkCreateBids(listId, pendingBulkBids)
            .then(() => {
                const sentKeys = new Set(pendingBulkBids.map((bid) => bid.key));
                setProducts((previousProducts) =>
                    previousProducts.filter((product) => !sentKeys.has(product.key))
                );
                toast.success(`${pendingBulkBids.length} oferte au fost trimise.`);
                refreshBiddingCounts();
            })
            .catch((error) => {
                console.error('Error submitting bulk offers:', error);
                const statusCode = error?.response?.status;
                if (statusCode === 401) {
                    loginService.removeToken();
                    localStorage.removeItem('userProfile');
                    toast.error('Sesiunea a expirat. Te rugam sa te autentifici din nou.');
                    navigate('/login');
                    return;
                }

                if (statusCode === 403) {
                    toast.error('Nu ai permisiune sa trimiti oferte sau bidding-ul este inchis.');
                    return;
                }

                toast.error('A aparut o eroare la trimiterea ofertelor.');
            })
            .finally(() => {
                setIsBulkSubmitting(false);
            });
    };

    const hideProduct = (record) => {
        const productId = record.originalData.id;
        const status = 'hide';
        bidingService.hideProduct(listId, productId, status)
            .then(() => {
                toast.success(`Produsul ${record.name} a fost ascuns.`);
                setProducts(prev => prev.filter(p => p.key !== record.key));
                refreshBiddingCounts();
            })
            .catch((err) => {
                console.error("Eroare la ascunderea produsului:", err);
                toast.error("A apărut o eroare la ascunderea produsului.");
            });
    };
    const columns = [
        {
            title: 'Nume produs',
            dataIndex: 'name',
            key: 'name',
            width: 280,
            className: 'biding__product-name-cell',
            render: renderProductNameCell
        },
        {
            title: 'Cod de bare',
            dataIndex: 'barcode',
            key: 'barcode',
            width: 230,
            render: (barcode) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span style={{ color: '#000' }}>{barcode}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            type="link"
                            onClick={() => {
                                navigator.clipboard.writeText(barcode);
                                toast.success('Codul de bare a fost copiat!');
                            }}
                            style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                        >
                            <span
                                style={{
                                    backgroundColor: 'gray',
                                    borderRadius: '50%',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                }}
                            >
                                <CopyOutlined style={{ color: '#fff', fontSize: '14px' }} />
                            </span>
                        </Button>
                    </div>
                </div>
            ),
        },
        {
            title: 'Cantitate',
            key: 'quantity',
            width: 130,
            className: 'biding__compact-cell',
            render: (_, record) => {
                const quantity = record.quantity ?? '-';
                const unit = record.unit_measurement ? ` ${record.unit_measurement}` : '';
                return `${quantity}${unit}`;
            },
        },
        createLastWinningPriceColumn(),
        {
            title: (
                <div className="biding__price-header">
                    <span>Oferta preț</span>
                    <small>TVA inclus</small>
                </div>
            ),
            key: 'offerPrice',
            width: 150,
            className: 'biding__compact-cell',
            render: (_, record) => {
                return (
                    <Input
                        type="number"
                        min={0}
                        value={offerPrices[record.key] ?? ''}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setOfferPrices((prev) => ({
                                ...prev,
                                [record.key]: nextValue
                            }));
                        }}
                        onPressEnter={() => handleSendOffer(record)}
                        placeholder="0.00"
                    />
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            width: 140,
            className: 'biding__compact-cell',
            render: (_, record) => (
                <Tag color={record.statusTag?.color || 'default'}>
                    {record.statusTag?.label || 'Necunoscut'}
                </Tag>
            ),
        },
        {
            title: 'Acțiuni',
            key: 'actions',
            width: 180,
            className: 'biding__compact-cell',
            render: (_, record) => {
                const hasCompletedOfferPrice =
                    offerPrices[record.key] !== undefined &&
                    offerPrices[record.key] !== null &&
                    String(offerPrices[record.key]).trim() !== '';

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button
                            type="primary"
                            onClick={() => handleSendOffer(record)}
                            disabled={record.hasBid || !hasCompletedOfferPrice}
                        >
                            {record.hasBid ? 'Ofertă trimisă' : 'Trimite ofertă'}
                        </Button>
                    </div>
                );
            },
        },
        {
            title: 'Ascunde',
            key: 'hide',
            width: 90,
            className: 'biding__compact-cell',
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<MinusOutlined style={{ color: 'red' }} />}
                    onClick={() => hideProduct(record)}
                    aria-label={`Ascunde ${record.name}`}
                />
            ),
        },
    ];

    return (
        <div className='standard products list biding'>
            <Header />
            <div className='standard__mobile-header'>
                <img src='/images/logo.png' alt='logo' className='standard__only-mobile standard__mobile-logo' />
            </div>

            <div className='standard__container list '>
                <SupplierMenu
                    categoryOptions={categoryOptions}
                    selectedCategories={selectedCategories}
                    onCategoriesChange={setSelectedCategories}
                />

                <div className='standard__wrapper biding__wrapper'>
                    <div className='biding__content'>
                        <div className='biding__header'>
                            <div className='biding__header-main'>
                                <h2 className='standard__page-title'>Bine ai venit pe platforma MECOM Express!</h2>
                                <h4 className='standard__page-info'>
                                    Verifică produsele necesare și trimite rapid ofertele tale. Confirmă cantitatea și cerințele înainte de trimitere.
                                </h4>
                            </div>
                            <div className='biding__header-side'>
                                <div className='biding__top-area'>
                                    <div className='list__search-box biding__search-box'>
                                        <Search
                                            className="list__search-input"
                                            placeholder="Caută produs..."
                                            onChange={(e) => handleSearch(e.target.value)}
                                            value={searchQuery}
                                            enterButton
                                            allowClear
                                        />
                                    </div>
                                    <div className='biding__switch'>
                                        <Switch
                                            checked={isAutocompleteEnabled}
                                            loading={isAutocompleteLoading}
                                            onChange={(checked) => setIsAutocompleteEnabled(checked)}
                                        />
                                        <span>Autocompletare prețuri din ofertele tale recente</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <AntMenu
                            mode="horizontal"
                            selectedKeys={[selectedMenuKey]}
                            onClick={(e) => {
                                switch (e.key) {
                                    case 'produse':
                                        navigate(`/produse-ofertare/${id}`);
                                        break;
                                    case 'ascunse':
                                        navigate(`/produse-ascunse/${id}`);
                                        break;
                                    case 'oferte':
                                        navigate(`/oferte-inregistrate/${id}`);
                                        break;
                                    default:
                                        break;
                                }
                            }}
                        >
                            <AntMenu.Item key="produse">
                                Produse pentru ofertare{typeof pickBiddingCount('remaining_items', 'remaining_items_count', 'biddable', 'biddable_count', 'for_bidding', 'for_bidding_count', 'offering', 'offering_count', 'products', 'products_count') === 'number' ? ` (${pickBiddingCount('remaining_items', 'remaining_items_count', 'biddable', 'biddable_count', 'for_bidding', 'for_bidding_count', 'offering', 'offering_count', 'products', 'products_count')})` : ''}
                            </AntMenu.Item>
                            <AntMenu.Item key="ascunse">
                                Produse ascunse{typeof pickBiddingCount('hidden', 'hidden_count', 'hidden_items', 'hidden_items_count', 'hidden_products', 'hidden_products_count') === 'number' ? ` (${pickBiddingCount('hidden', 'hidden_count', 'hidden_items', 'hidden_items_count', 'hidden_products', 'hidden_products_count')})` : ''}
                            </AntMenu.Item>
                            <AntMenu.Item key="oferte">
                                Oferte inregistrate{typeof pickBiddingCount('offered_items', 'offered_items_count', 'bid_items', 'bid_items_count', 'bids', 'bids_count', 'offered', 'offered_count', 'registered', 'registered_count') === 'number' ? ` (${pickBiddingCount('offered_items', 'offered_items_count', 'bid_items', 'bid_items_count', 'bids', 'bids_count', 'offered', 'offered_count', 'registered', 'registered_count')})` : ''}
                            </AntMenu.Item>
                        </AntMenu>
                        <div className="list__table-wrapper">
                            <div className='list__table-content' ref={tableContentRef}>
                                <Table
                                    dataSource={filteredProducts}
                                    columns={columns}
                                    pagination={false}
                                    rowKey="key"
                                    loading={loading}
                                    scroll={{ ...(tableScrollConfig || {}), x: 1780 }}
                                    tableLayout="fixed"
                                />
                            </div>
                            <div className='biding__bottom-wrapper' ref={footerRef}>
                                <div className='biding__time-box'>
                                    <div className='biding__time'>
                                        <h5>Timp ramas pentru ofertare: </h5>
                                        <h4> {timeLeft}</h4>                                </div>
                                </div>
                                <button
                                    className='biding__send-offer'
                                    onClick={handleBulkSendOffers}
                                    disabled={!canSubmitBulkBids}
                                >
                                    <img src="/images/right.png" alt="" />
                                    <h5>{isBulkSubmitting ? 'Se trimit...' : 'Trimite oferte'}</h5>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default OfertedProducts;